/**
 * Zod's JIT probe, turned off for pages that ship a strict CSP — Task 11.4, requirements 6.5 and
 * 15.6–15.7.
 *
 * The side-effect form the frontends actually import is `./interpreted-schemas.ts`; this module holds the
 * call itself so it can be imported and asserted without triggering it.
 *
 * ## What this is working around
 *
 * Zod compiles validators with `new Function` when the environment allows it, and decides whether it
 * does by trying: `try { Function(''); return true } catch { return false }`. Under `script-src 'self'`
 * with no `'unsafe-eval'` that call throws, Zod catches it, and every schema falls back to the
 * interpreted path. Validation is correct either way — nothing is broken by the probe.
 *
 * What the probe *does* do is trip the policy. A blocked `Function` call fires
 * `securitypolicyviolation` and logs a console error before the `catch` runs, because CSP enforcement
 * happens at the call, not at the throw. So a page that behaves perfectly reports a violation on every
 * load, and any check that treats violations as failures — `apps/e2e/csp`, and a browser console a
 * developer is reading — sees noise it cannot act on. Worse, real violations hide in it.
 *
 * Zod's own source names this case and offers the way out, so this is the supported path rather than a
 * workaround: setting `jitless` skips the probe and goes straight to the interpreted validators.
 *
 * ## Why it is not applied here at import time
 *
 * `jitless` is global to the Zod instance, and the API wants the JIT: it runs in Node under no CSP, and
 * it parses every request and response body. Making this module's import apply it would quietly slow the
 * server down to fix a browser's console. So it is an explicit call, made by the two browser entry
 * points and nowhere else.
 */
import { config } from 'zod';
/**
 * Call once, before any schema is constructed, from a browser entry point.
 *
 * Deliberately not named `use…`: that prefix is reserved for React Hooks, and `react-hooks` rejects a
 * call to one at module top level — which is exactly where this has to be called.
 */
export const disableSchemaJit = () => {
    config({ jitless: true });
};
//# sourceMappingURL=strict-csp.js.map